import { useEffect, useState } from "react";
import { apiFetch } from "../config/api";
import Loader from "./Loader";

function CreateTaskModal({ open, onClose, onCreated, projectId = null, phaseId = null }) {
  const [users, setUsers] = useState([]);

  const [usersLoading, setUsersLoading] = useState(false);

  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState("");

  const [title, setTitle] = useState("");
  const [brief, setBrief] = useState("");
  const [assignedToUserId, setAssignedToUserId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState("Medium");
  const [expectedOutcome, setExpectedOutcome] = useState("");

  useEffect(() => {
    if (open) {
      loadAssignableUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadAssignableUsers = async () => {
    setUsersLoading(true);
    try {
      const res = await apiFetch("/api/users/assignable");
      if (!res.ok) throw new Error("Failed to load users");
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const createTask = async () => {
    if (creating) return;

    if (!title.trim() || !assignedToUserId) {
      setFormError("Title and assignee are required.");
      return;
    }
    setFormError("");

    try {
      setCreating(true);

      const res = await apiFetch("/api/tasks", {
        method: "POST",
        body: JSON.stringify({
          title,
          projectId: projectId || null,
          phaseId: phaseId || null,
          assignedToUserId,
          brief: brief.trim() || null,
          dueDate: dueDate || null,
          priority,
          expectedOutcome: expectedOutcome || null,
        }),
      });

      if (!res.ok) {
        const msg = await res.text();
        console.error(msg);
        throw new Error("Failed to create task");
      }

      setTitle("");
      setAssignedToUserId("");
      setBrief("");
      setDueDate("");
      setPriority("Medium");
      setExpectedOutcome("");

      onClose();
      onCreated(); // refresh everywhere
    } catch (err) {
      setFormError(err.message || "Could not create task. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={onClose}
      onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}>
      <div className="glass-card modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{projectId ? "Add Task to Project" : "Create Task"}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="form-group">
          <input
            className="form-input"
            placeholder="Task title *"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
          />
          <div style={{ textAlign: "right", fontSize: 11, color: title.length > 180 ? "#dc2626" : "var(--text-3)", marginTop: 3 }}>
            {title.length} / 200
          </div>
        </div>

        <div className="form-group">
          <input
            className="form-input"
            placeholder="Brief (short description shown below title)"
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            maxLength={500}
          />
        </div>

        <div className="form-group">
          <select
            className="form-input"
            value={assignedToUserId}
            onChange={(e) => setAssignedToUserId(e.target.value)}
          >
            <option value="">
              {usersLoading ? "Loading users..." : "Assign to *"}
            </option>
            {usersLoading
              ? null
              : users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name || u.email}
                  </option>
                ))}
          </select>
          {usersLoading && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, fontSize: 12, color: "var(--text-3)" }}>
              <Loader size="sm" inline />
              Loading...
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Due Date</label>
          <input
            className="form-input"
            type="date"
            value={dueDate}
            min={new Date().toLocaleDateString('en-CA')}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Priority</label>
          <select
            className="form-input"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          >
            <option value="High">🔴 High</option>
            <option value="Medium">🟡 Medium</option>
            <option value="Low">🟢 Low</option>
          </select>
        </div>

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Expected Outcome</label>
          <textarea
            className="form-input"
            placeholder="What should be delivered?"
            value={expectedOutcome}
            onChange={(e) => setExpectedOutcome(e.target.value)}
            rows={3}
          />
        </div>

        {formError && <div className="error-banner">{formError}</div>}

        <div className="modal-actions">
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={createTask} disabled={creating}>
            {creating && (
              <Loader size="sm" inline style={{ marginRight: 8 }} />
            )}
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateTaskModal;
